import React, { useState, useEffect, useMemo, useRef } from "react"
import { Select, Button, Space, Tag, Tooltip, message } from "antd"
import {
  PlusOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons"
import { Cities } from "../../common/data/Cities"
import { Section } from "../../common/data/Section"

const PROVINCE_ID = 13 // Kurdistan

const MultiLocation = ({ value, onChange, disabled = false }) => {
    
  // Each row: { cityId, sectionId, key }
  const [rows, setRows] = useState([])
  // Tracks whether the last value change was triggered internally (by notifyChange)
  const isInternalChange = useRef(false)
  // Draft row for adding new entry
  const [draftCityId, setDraftCityId] = useState(undefined)
  const [draftSectionId, setDraftSectionId] = useState(undefined)
  const [draftSectionList, setDraftSectionList] = useState([])

  // City list filtered by province
  const cityList = useMemo(
    () =>
      Cities.filter(c => c.Id_Province === PROVINCE_ID).map(c => ({
        label: c.CityName,
        value: c.Id,
      })),
    []
  )

  // Initialize rows from value (sectionIds) — re-run when value changes from parent
  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false
      return
    }
    if (value && Array.isArray(value) && value.length > 0) {
      const newRows = value
        .map((sectionId, index) => {
          const sec = Section.find(s => s.Id === sectionId)
          if (!sec) return null
          const city = Cities.find(c => c.Id === sec.Id_City)
          if (!city) return null
          return {
            key: Date.now() + index,
            cityId: city.Id,
            sectionId: sec.Id,
            cityName: city.CityName,
            sectionName: sec.SectionName,
          }
        })
        .filter(Boolean)
      setRows(newRows)
    } else {
      setRows([])
    }
  }, [JSON.stringify(value)]) // eslint-disable-line

  // Notify parent of changes
  const notifyChange = updatedRows => {
    isInternalChange.current = true
    const sectionIds = updatedRows.map(r => r.sectionId)
    onChange && onChange(sectionIds)
  }

  // Handle draft city change
  const handleDraftCityChange = cityId => {
    setDraftCityId(cityId)
    setDraftSectionId(undefined)
    const sections = Section.filter(s => s.Id_City === cityId)
    setDraftSectionList(
      sections.map(s => ({ label: s.SectionName, value: s.Id }))
    )
  }

  // Handle draft section change
  const handleDraftSectionChange = sectionId => {
    setDraftSectionId(sectionId)
  }

  // Add new row from draft
  const handleAdd = () => {
    if (!draftCityId || !draftSectionId) {
      message.warning("لطفاً شهر و بخش را انتخاب کنید")
      return
    }

    const city = Cities.find(c => c.Id === draftCityId)
    const selectedSec = Section.find(s => s.Id === draftSectionId)

    // If "مرکزی" is selected, add ALL sections for this city (including مرکزی itself)
    if (selectedSec?.SectionName === "مرکزی") {
      const allSectionsForCity = Section.filter(s => s.Id_City === draftCityId)
      if (allSectionsForCity.length === 0) {
        // Fallback: just add مرکزی itself
        const isDuplicate = rows.some(
          r => r.cityId === draftCityId && r.sectionId === draftSectionId
        )
        if (isDuplicate) {
          message.warning("این شهر و بخش قبلاً اضافه شده است")
          return
        }
        const newRow = {
          key: Date.now(),
          cityId: draftCityId,
          sectionId: draftSectionId,
          cityName: city?.CityName || "",
          sectionName: selectedSec?.SectionName || "",
        }
        const updatedRows = [...rows, newRow]
        setRows(updatedRows)
        notifyChange(updatedRows)
      } else {
        // Add all sections (including مرکزی) that aren't already added
        const newRows = allSectionsForCity
          .filter(s => !rows.some(r => r.cityId === draftCityId && r.sectionId === s.Id))
          .map((s, i) => ({
            key: Date.now() + i + 1,
            cityId: draftCityId,
            sectionId: s.Id,
            cityName: city?.CityName || "",
            sectionName: s.SectionName,
          }))
        if (newRows.length === 0) {
          message.warning("تمام بخش‌های این شهر قبلاً اضافه شده‌اند")
          return
        }
        const updatedRows = [...rows, ...newRows]
        setRows(updatedRows)
        notifyChange(updatedRows)
      }
    } else {
      // Normal add: single section
      const isDuplicate = rows.some(
        r => r.cityId === draftCityId && r.sectionId === draftSectionId
      )
      if (isDuplicate) {
        message.warning("این شهر و بخش قبلاً اضافه شده است")
        return
      }

      const newRow = {
        key: Date.now(),
        cityId: draftCityId,
        sectionId: draftSectionId,
        cityName: city?.CityName || "",
        sectionName: selectedSec?.SectionName || "",
      }

      const updatedRows = [...rows, newRow]
      setRows(updatedRows)
      notifyChange(updatedRows)
    }

    // Reset draft
    setDraftCityId(undefined)
    setDraftSectionId(undefined)
    setDraftSectionList([])
  }

  // Remove a row
  const handleRemove = key => {
    const updatedRows = rows.filter(r => r.key !== key)
    setRows(updatedRows)
    notifyChange(updatedRows)
  }

  return (
    <div>
      {/* Selected locations header */}
      {rows.length > 0 && (
        <div
          style={{
            marginBottom: "12px",
            padding: "8px 12px",
            background: "#f6ffed",
            borderRadius: "8px",
            border: "1px solid #b7eb8f",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              color: "#52c41a",
              marginBottom: "6px",
              fontWeight: "600",
            }}
          >
            <EnvironmentOutlined style={{ marginLeft: "4px" }} />
            مناطق انتخاب شده ({rows.length})
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
            {rows.map(row => (
              <Tag
                key={row.key}
                color="green"
                closable={!disabled}
                onClose={() => handleRemove(row.key)}
                style={{
                  borderRadius: "6px",
                  padding: "4px 8px",
                  fontSize: "13px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <EnvironmentOutlined />
                {row.cityName} - {row.sectionName}
              </Tag>
            ))}
          </div>
        </div>
      )}

      {/* Add new row */}
      {!disabled && (
        <div
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Select
            placeholder="انتخاب شهر"
            value={draftCityId}
            onChange={handleDraftCityChange}
            options={cityList}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").includes(input)
            }
            style={{ flex: 1, minWidth: "140px" }}
            size="middle"
            allowClear
          />
          <Select
            placeholder="انتخاب بخش"
            value={draftSectionId}
            onChange={handleDraftSectionChange}
            options={draftSectionList}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").includes(input)
            }
            style={{ flex: 1, minWidth: "140px" }}
            size="middle"
            disabled={!draftCityId}
            allowClear
          />
          <Tooltip title="افزودن منطقه">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              disabled={!draftCityId || !draftSectionId}
              style={{
                borderRadius: "8px",
                background:
                  draftCityId && draftSectionId
                    ? "linear-gradient(135deg, #52c41a 0%, #389e0d 100%)"
                    : undefined,
                border: "none",
              }}
            />
          </Tooltip>
        </div>
      )}
    </div>
  )
}

export default MultiLocation
